import React, { useEffect, useRef, useState } from 'react';
import { useLocation, Link } from 'react-router';
import { observer } from 'mobx-react';
import styles from '../HomeRedesign/CategoryPageRedesign/CategoryPageRedesign.module.scss';
import Header from '../HomeRedesign/components/Header/Header';
import ScrollToTop from '../HomeRedesign/components/ScrollToTop/ScrollToTop';
import Footer from '../HomeRedesign/components/Footer/Footer';
import Product from '@models/product';
import { generateProductDetailsUrl } from '@utils/urlUtils';
import {
  formatDiscountPercentage,
  truncateDiscountPercent,
} from '@utils/formatDiscountPercentage';
import { pageTrackingService } from '../../services/pageTracking';
import { analytics } from '../../services/analytics';
import { storesAPI } from '@api/main';
import { cartStore, authStore, wishlistStore } from '@stores/main';
import { useToast } from '../../contexts/ToastContext';
import { useBreadcrumbNavigation } from '../../utils/breadcrumbNavigation';
import { gtagService } from '../../services/gtag';
import { allCategories } from '../../data/categories';

type SortOption = 'newest' | 'popular' | 'price-low' | 'price-high';

const mapSortToOrderBy = (sortOption: SortOption): string => {
  switch (sortOption) {
    case 'newest':
      return 'created:desc';
    case 'popular':
      return 'popularity:desc';
    case 'price-low':
      return 'price:asc';
    case 'price-high':
      return 'price:desc';
    default:
      return 'created:desc';
  }
};

const SearchPage: React.FC = observer(() => {
  const location = useLocation();
  const { showToast } = useToast();
  const { goToLogin } = useBreadcrumbNavigation();

  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [sortBy, setSortBy] = useState<SortOption>('popular');
  // One search_performed per query — pagination/sort re-runs of the fetch
  // effect must not re-emit (the old Countly layer deduped this).
  const lastTrackedSearchRef = useRef<string | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [imageErrors, setImageErrors] = useState<Set<string>>(new Set());
  const [addingToCart, setAddingToCart] = useState<Set<string>>(new Set());
  const [optimisticCartIds, setOptimisticCartIds] = useState<Set<string>>(new Set());
  const [updatingWishlist, setUpdatingWishlist] = useState<Set<string>>(new Set());

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', 'dark');
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [location.search]);

  useEffect(() => {
    let cancelled = false;

    const fetchProducts = async () => {
      try {
        setLoading(true);
        setError(null);
        const searchQuery = new URLSearchParams(location.search).get('q') || '';
        const categoryParam = new URLSearchParams(location.search).get('category') || '';
        const finalSearchQuery = searchQuery || '';

        let filters: { categories: string[] } | undefined;
        if (categoryParam) {
          const category = allCategories.find((cat) => cat.id === categoryParam);
          if (category) {
            filters = { categories: [category.name] };
          }
        }

        const orderBy = mapSortToOrderBy(sortBy);
        const result = await storesAPI.searchProducts(finalSearchQuery, page, filters, orderBy);

        if (cancelled) return;
        setProducts(result.items);
        setTotalItems(result.total);

        const category = categoryParam ? allCategories.find((cat) => cat.id === categoryParam) : null;
        const trackingQuery =
          categoryParam && !searchQuery
            ? category?.displayName || category?.name || categoryParam
            : finalSearchQuery;
        if (lastTrackedSearchRef.current !== trackingQuery) {
          lastTrackedSearchRef.current = trackingQuery;
          pageTrackingService.trackSearchView(trackingQuery, result.total);
        }
      } catch (err) {
        if (cancelled) return;
        setError('Failed to fetch products. Please try again later.');
        console.error('Error fetching products:', err);
        setProducts([]);
        setTotalItems(0);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchProducts();
    return () => {
      cancelled = true;
    };
  }, [page, location.search, sortBy]);

  const searchParams = new URLSearchParams(location.search);
  const searchQuery = searchParams.get('q') || '';
  const categoryParam = searchParams.get('category') || '';
  const category = categoryParam && !searchQuery ? allCategories.find((cat) => cat.id === categoryParam) : null;
  const displayQuery =
    categoryParam && !searchQuery ? category?.displayName || category?.name || categoryParam : searchQuery;

  const pageTitle = displayQuery.trim()
    ? `Search results for "${displayQuery}"`
    : 'Best discounts on Oneplay Store';
  const pageSubtitle = loading
    ? 'Loading products...'
    : `${totalItems} ${totalItems === 1 ? 'product' : 'products'} found`;

  const convertProductToCardFormat = (product: Product) => {
    const productUrl = product.id
      ? generateProductDetailsUrl({
          id: product.id,
          title: product.title || product.extendedTitle || 'Product',
        })
      : '#';
    // Use API discount, or compute from prices when there's a price drop
    const hasPriceDrop = product.displayPrice > product.buyPrice && product.displayPrice > 0;
    const computedDiscount = hasPriceDrop
      ? truncateDiscountPercent(
          ((product.displayPrice - product.buyPrice) / product.displayPrice) * 100
        )
      : 0;
    const discount =
      product.discountPercentage != null && product.discountPercentage > 0
        ? product.discountPercentage
        : computedDiscount > 0
          ? computedDiscount
          : undefined;
    return {
      id: product.id,
      name: product.title || product.extendedTitle || 'Product',
      price: product.buyPriceString || `₹${product.buyPrice}`,
      originalPrice:
        product.displayPriceString && product.displayPrice !== product.buyPrice
          ? product.displayPriceString
          : undefined,
      discount,
      gradient: product.background || 'linear-gradient(135deg, #42A5F5, #1E88E5)',
      imageUrl: product.poster || undefined,
      icon: 'fas fa-gamepad',
      tag: product.hotOffer ? 'Hot' : product.buttonDiscountText || undefined,
      link: productUrl,
    };
  };

  const handleProductClick = (product: Product, position: number) => {
    pageTrackingService.trackSearchResultClick(product.id, product.title, position);
  };

  const isInWishlist = (productId?: string) =>
    !!productId && wishlistStore.wishlist.some((item) => item.id === productId);

  const getCartQuantity = (productId?: string) => {
    if (!productId) return 0;
    const id = String(productId);
    const cartItem = cartStore.items.find((item) => String(item.id) === id);
    const fromStore = Math.max(0, cartItem?.isInCartQuantity ?? cartItem?.quantity ?? 0);
    if (fromStore > 0) return fromStore;
    return optimisticCartIds.has(id) ? 1 : 0;
  };

  const isInCart = (product: Product) => getCartQuantity(product.id) > 0;

  const handleWishlistToggle = async (e: React.MouseEvent, product: Product) => {
    e.preventDefault();
    e.stopPropagation();
    if (!product.id) return;
    if (!authStore.isAuthenticated) {
      goToLogin();
      return;
    }
    setUpdatingWishlist((prev) => new Set(prev).add(product.id));
    try {
      const inWishlist = wishlistStore.wishlist.some((item) => item.id === product.id);
      if (inWishlist) {
        wishlistStore.removeProductFromWishlist(product);
        showToast(`Removed from wishlist`, 'info', 2500);
      } else {
        await wishlistStore.addProductToWishlist(product);
      }
    } catch (error) {
      console.error("Error updating wishlist:", error);
    } finally {
      setUpdatingWishlist((prev) => {
        const next = new Set(prev);
        next.delete(product.id);
        return next;
      });
    }
  };

  const handleAddToCart = async (product: Product, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!authStore.isAuthenticated) {
      goToLogin();
      return;
    }
    if (!product.id) return;
    const productIdStr = String(product.id);
    setAddingToCart((prev) => new Set(prev).add(product.id));
    setOptimisticCartIds((prev) => new Set(prev).add(productIdStr));
    try {
      const res = await cartStore.addItemToCart(product.id);
      if (!res) {
        return;
      }
      pageTrackingService.trackProductAddToCart(product.id, product.title, 'search');
      gtagService.trackAddToCart({
        currency: product.currency || 'USD',
        value: product.buyPrice,
        items: [
          { item_id: product.id, item_name: product.title, price: product.buyPrice, quantity: 1 },
        ],
      });
    } catch (error) {
      console.error("Error adding to cart:", error);
    } finally {
      setAddingToCart((prev) => {
        const next = new Set(prev);
        next.delete(product.id);
        return next;
      });
      setOptimisticCartIds((prev) => {
        const next = new Set(prev);
        next.delete(productIdStr);
        return next;
      });
    }
  };

  const handleSortChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    analytics.track('search_sorted', { sort: e.target.value });
    setSortBy(e.target.value as SortOption);
    setPage(1);
  };

  const handleImageError = (productId: string) => {
    setImageErrors((prev) => new Set(prev).add(productId));
  };

  const productPlaceholderUrl = `${process.env.PUBLIC_URL || ''}/images/product-placeholder.svg`;

  return (
    <div className={styles.categoryPage}>
      <Header />

      <div className={styles.contentWrapper}>
        <div className={styles.headerRowWithFilters}>
          <div className={styles.pageTitleSection}>
            <h1 className={styles.pageTitle}>{pageTitle}</h1>
            <p className={styles.pageSubtitle}>{pageSubtitle}</p>
          </div>
          <div className={styles.searchHeaderControls}>
            <select
              id="search-sort"
              className={styles.searchHeaderSort}
              value={sortBy}
              onChange={handleSortChange}
              aria-label="Sort results"
            >
              <option value="newest">Newest First</option>
              <option value="popular">Most Popular</option>
              <option value="price-low">Price: Low to High</option>
              <option value="price-high">Price: High to Low</option>
            </select>
            <div className={styles.searchHeaderViewToggle} role="group" aria-label="View mode">
              <button
                type="button"
                className={viewMode === 'grid' ? styles.searchHeaderViewBtnActive : styles.searchHeaderViewBtn}
                onClick={() => setViewMode('grid')}
                aria-label="Grid view"
                title="Grid view"
              >
                <i className="fas fa-th-large" aria-hidden />
              </button>
              <button
                type="button"
                className={viewMode === 'list' ? styles.searchHeaderViewBtnActive : styles.searchHeaderViewBtn}
                onClick={() => setViewMode('list')}
                aria-label="List view"
                title="List view"
              >
                <i className="fas fa-list" aria-hidden />
              </button>
            </div>
          </div>
        </div>

        <div className={styles.searchResultsArea}>
          {loading ? (
            <div className={styles.loadingState}>
              <div className={styles.loadingSpinner} aria-hidden />
              <p>Loading products...</p>
            </div>
          ) : error ? (
            <div className={styles.emptyState}>
              <i className="fas fa-exclamation-circle" aria-hidden />
              <h3>{error}</h3>
              <p>Please try again later.</p>
            </div>
          ) : products.length === 0 ? (
            <div className={styles.emptyState}>
              <i className="fas fa-search" aria-hidden />
              <h3>No products found</h3>
              <p>
                {displayQuery.trim()
                  ? `No results for "${displayQuery}". Try different keywords.`
                  : 'Check back later for best discounts.'}
              </p>
              <Link to="/" className={styles.backLink}>
                Back to Home
              </Link>
            </div>
          ) : (
            <div className={`${styles.productsGrid} ${styles[viewMode]}`}>
            {products.map((product, index) => {
              const cardProduct = convertProductToCardFormat(product);
              const productKey = product.id || `product-${index}`;
              return (
                <Link
                  key={productKey}
                  to={cardProduct.link || '#'}
                  className={styles.productCard}
                  onClick={() => handleProductClick(product, index + 1)}
                >
                  <div
                    className={`${styles.productImage} ${cardProduct.imageUrl ? styles.hasImage : ''}`}
                    style={{
                      background:
                        cardProduct.imageUrl && !imageErrors.has(productKey)
                          ? 'transparent'
                          : cardProduct.gradient,
                    }}
                  >
                    {cardProduct.discount != null && cardProduct.discount > 0 && (
                      <div className={styles.discountBadge}>
                        {formatDiscountPercentage(cardProduct.discount)}% OFF
                      </div>
                    )}
                    {viewMode === 'grid' && (
                      <button
                        type="button"
                        className={`${styles.wishlistBtn} ${isInWishlist(product.id) ? styles.wishlistBtnActive : ''}`}
                        onClick={(e) => handleWishlistToggle(e, product)}
                        disabled={updatingWishlist.has(product.id || '')}
                        aria-label={isInWishlist(product.id) ? 'Remove from wishlist' : 'Add to wishlist'}
                      >
                        {updatingWishlist.has(product.id || '') ? (
                          <i className="fas fa-spinner fa-spin" aria-hidden />
                        ) : (
                          <i className="fas fa-heart" aria-hidden />
                        )}
                      </button>
                    )}
                    {cardProduct.imageUrl && !imageErrors.has(productKey) ? (
                      <img
                        src={cardProduct.imageUrl}
                        alt={cardProduct.name}
                        className={styles.productImageContent}
                        onError={() => handleImageError(productKey)}
                        onLoad={(e) => {
                          const img = e.target as HTMLImageElement;
                          if (img.naturalWidth === 0 || img.naturalHeight === 0) {
                            handleImageError(productKey);
                          }
                        }}
                      />
                    ) : (
                      <img
                        src={productPlaceholderUrl}
                        alt={cardProduct.name}
                        className={styles.productImageContent}
                      />
                    )}
                  </div>
                  <div className={styles.productInfo}>
                    <div className={styles.productName} title={cardProduct.name}>
                      {cardProduct.name}
                    </div>
                    <div className={styles.productPriceContainer}>
                      {viewMode === 'list' ? (
                        <div className={styles.productPriceGroup}>
                          <span className={styles.productPrice}>{cardProduct.price}</span>
                          {cardProduct.originalPrice && (
                            <span className={styles.originalPrice}>{cardProduct.originalPrice}</span>
                          )}
                        </div>
                      ) : (
                        <>
                          <span className={styles.productPrice}>{cardProduct.price}</span>
                          {cardProduct.originalPrice && (
                            <span className={styles.originalPrice}>{cardProduct.originalPrice}</span>
                          )}
                        </>
                      )}
                      <div className={styles.productPriceActions}>
                        {viewMode === 'list' && (
                          <button
                            type="button"
                            className={`${styles.wishlistBtn} ${isInWishlist(product.id) ? styles.wishlistBtnActive : ''}`}
                            onClick={(e) => handleWishlistToggle(e, product)}
                            disabled={updatingWishlist.has(product.id || '')}
                            aria-label={isInWishlist(product.id) ? 'Remove from wishlist' : 'Add to wishlist'}
                          >
                            {updatingWishlist.has(product.id || '') ? (
                              <i className="fas fa-spinner fa-spin" aria-hidden />
                            ) : (
                              <i className="fas fa-heart" aria-hidden />
                            )}
                          </button>
                        )}
                        <button
                          type="button"
                          className={`${styles.addToCartIcon} ${isInCart(product) ? styles.addToCartIconInCart : ''}`}
                          onClick={(e) => handleAddToCart(product, e)}
                          disabled={addingToCart.has(product.id || '') || !product.id}
                          aria-label={isInCart(product) ? 'In cart' : 'Add to Cart'}
                          title={isInCart(product) ? `In cart (${getCartQuantity(product.id)})` : 'Add to Cart'}
                        >
                          {addingToCart.has(product.id || '') ? (
                            <i className="fas fa-spinner fa-spin" aria-hidden />
                          ) : isInCart(product) ? (
                            <>
                              <i className="fas fa-shopping-cart" aria-hidden />
                              {getCartQuantity(product.id) > 1 && (
                                <span className={styles.cartQuantityBadge}>{getCartQuantity(product.id)}</span>
                              )}
                            </>
                          ) : (
                            <img
                              src={`${process.env.PUBLIC_URL}/images/icons/add-to-cart.svg`}
                              alt="Add to Cart"
                              width={24}
                              height={24}
                            />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
          )}
        </div>
      </div>

      <Footer />
      <ScrollToTop />
    </div>
  );
});

export default SearchPage;
